// IMPORTANT: This file should use the centralized config from ../config.js
// Import the main config to ensure consistency
import { getApiUrl as getMainApiUrl } from '../config';

// Use the centralized config function for dynamic API URL detection
const getDynamicApiUrl = () => {
  return getMainApiUrl();
};

// Export API_URL using dynamic detection
export const API_URL = getDynamicApiUrl();


// API URL being used
// This approach ensures consistent API URL usage across the application
// while still allowing for configuration via environment variables

// Base URL for the backend server (same as API_URL since we don't add /api)
const BASE_URL = API_URL;

// Helper function to get a full API URL
export const getApiUrl = (path) => {
  // If path already starts with http or https, return it as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Check if path already includes /api prefix
  if (path.startsWith('/api/')) {
    // Remove the /api prefix to prevent duplication
    const pathWithoutApiPrefix = path.substring(4); // Remove '/api'
    return `${API_URL}${pathWithoutApiPrefix}`;
  }
  
  // Otherwise, use API_URL with the path as is
  return `${API_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

// Helper function to get a full file URL
export const getFileUrl = (path) => {
  // If path already starts with http or https, return it as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Otherwise, prepend the BASE_URL
  return `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

// Helper function to handle API errors with user-friendly messages
export const handleApiError = (error) => {
  // Network or connection errors
  if (error.message === 'Network Error' || !navigator.onLine) {
    return "Our servers are temporarily unavailable. Please check your internet connection and refresh the page.";
  }
  
  // Request was made but server responded with an error
  if (error.response) {
    // Server returned an error response (4xx, 5xx)
    const status = error.response.status;
    
    if (status >= 500) {
      return "Our servers are temporarily unavailable. Please try again in a few moments.";
    }
    
    if (status === 404) {
      return "The requested resource was not found. Please refresh the page and try again.";
    }
    
    if (status === 401 || status === 403) {
      return "You don't have permission to access this resource. Please log in again.";
    }
    
    // Use the server's error message if available
    if (error.response.data && error.response.data.message) {
      const data = error.response.data;

      // Format stock availability errors nicely
      if (data.outOfStockItems || data.insufficientStockItems) {
        const lines = [data.message, ''];

        if (data.outOfStockItems && data.outOfStockItems.length > 0) {
          lines.push('Out of stock:');
          data.outOfStockItems.forEach(item => {
            lines.push(`• ${item.title} (requested: ${item.requested})`);
          });
          lines.push('');
        }

        if (data.insufficientStockItems && data.insufficientStockItems.length > 0) {
          lines.push('Limited stock:');
          data.insufficientStockItems.forEach(item => {
            lines.push(`• ${item.title} - only ${item.available} available (you requested ${item.requested})`);
          });
        }

        return lines.join('\n');
      }

      // If there are additional details, append them
      if (data.details) {
        return `${data.message}\n\n${data.details}`;
      }

      return data.message;
    }
  }
  
  // For other types of errors, provide a generic message
  return "Something went wrong. Please refresh the page and try again.";
};